/**
 * useNutritionReadiness: the user's nutrition readiness from the backend
 * (GET /profile/nutrition-readiness), with a short per-user cache so several
 * prompts on one screen share one request. `reload()` always refetches.
 *
 * status: 'loading' | 'loaded' | 'error'
 */
import { useCallback, useEffect, useState } from 'react';
import { profileAPI } from '../utils/api';
import useUserStore from '../store/userStore';
import { isReadiness } from '../utils/nutritionReadiness';

var CACHE_MS = 30000;
var cache = { userId: null, at: 0, value: null, inflight: null };

export function invalidateNutritionReadiness() {
  cache = { userId: null, at: 0, value: null, inflight: null };
}

export function fetchNutritionReadiness(userId, force) {
  var fresh = cache.userId === userId && cache.value && Date.now() - cache.at < CACHE_MS;
  if (!force && fresh) return Promise.resolve(cache.value);
  if (!force && cache.userId === userId && cache.inflight) return cache.inflight;
  var request = profileAPI.getNutritionReadiness().then(function (res) {
    var value = res && res.data;
    if (!isReadiness(value)) throw new Error('Unexpected nutrition readiness response');
    if (cache.inflight === request) cache = { userId: userId, at: Date.now(), value: value, inflight: null };
    return value;
  }, function (err) {
    if (cache.inflight === request) cache.inflight = null;
    throw err;
  });
  cache = { userId: userId, at: cache.at, value: cache.userId === userId ? cache.value : null, inflight: request };
  return request;
}

export default function useNutritionReadiness(enabled) {
  var userId = useUserStore(function (s) { return s.user ? s.user.user_id : null; });
  var [state, setState] = useState({ status: 'loading', readiness: null });

  var load = useCallback(function (force) {
    setState({ status: 'loading', readiness: null });
    return fetchNutritionReadiness(userId, force).then(function (value) {
      setState({ status: 'loaded', readiness: value });
      return value;
    }, function () {
      setState({ status: 'error', readiness: null });
      return null;
    });
  }, [userId]);

  useEffect(function () {
    if (enabled === false) return undefined;
    var alive = true;
    fetchNutritionReadiness(userId, false).then(function (value) {
      if (alive) setState({ status: 'loaded', readiness: value });
    }, function () {
      if (alive) setState({ status: 'error', readiness: null });
    });
    return function () { alive = false; };
  }, [userId, enabled]);

  var reload = useCallback(function () { return load(true); }, [load]);
  return { status: state.status, readiness: state.readiness, reload: reload };
}
